import React, { useState, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2, Check, User, Wallet, Building2, Upload, FileText, ExternalLink, MapPin, Users, UserPlus, Trash2, Sparkles, Home, Gauge, Landmark, FolderOpen } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { loanProfilingApi } from '@/services/api';
import INDIAN_BANKS from '@/utils/indianBanks';
import { PlacesAutocomplete } from '@/components/ui/PlacesAutocomplete';
import { SectionHeading } from './CockpitUI';
import { CATEGORY_FIELDS, INCOME_CATEGORIES } from './incomeCategoryFields';
import { CibilAnalysis } from './CibilAnalysis';
import { IncomeProfileBlock } from './ProfileSteps';
import { BankStatementAnalysis } from './BankStatementAnalysis';
import { DocumentsTab } from './DocumentsTab';

const reveal = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25 } };
const inputCls = 'flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 transition-all';

const ADDRESS_PROOFS = ['National ID/UID', 'Passport', 'Voter ID', 'Driving Licence'];
const GENDERS = ['Male', 'Female', 'Other'];
const MARITAL = ['Single', 'Married', 'Divorced', 'Widowed'];
const STABILITY = ['Less than 1 year', '1 year', '2 years', '3 years', '4 years', '5 years', 'More than 5 years', 'More than 10 years'];
const INCOME_PROOF = ['Yes', 'No'];
const CATEGORIES = INCOME_CATEGORIES;
const RESIDENCE = ['Owned', 'Rental', 'Parental', 'Company Provided', 'Other'];
const PROPERTY_OWNERS = ['Self', 'Father', 'Mother', 'Spouse', 'Jointly held', 'Other'];
const YES_NO = ['Yes', 'No'];
const RELATIONSHIPS = ['Spouse', 'Parent', 'Sibling', 'Son', 'Daughter', 'Other'];
const blankCoApplicant = () => ({
  name: '', relationship: '', mobile: '', cibil_score: '', category: '',
  income_proof: '', monthly_income: '', bank_statement_doc_id: '', bank_statement_doc_name: '',
});

const Field = ({ label, required, children }) => (
  <div className="flex flex-col space-y-1.5">
    <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
      {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </Label>
    {children}
  </div>
);

const SelectField = ({ label, required, value, onChange, options, placeholder, testId }) => (
  <Field label={label} required={required}>
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className={inputCls} data-testid={testId}>
        <SelectValue placeholder={placeholder || 'Select…'} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o} data-testid={`${testId}-opt-${o}`}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </Field>
);

const BankCombobox = ({ value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="flex h-10 w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 hover:bg-slate-50 transition-colors">
          <span className={`truncate ${value ? 'text-slate-900' : 'text-slate-400'}`}>{value || 'Select bank…'}</span>
          <Building2 className="w-4 h-4 text-slate-400 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search bank…" data-testid="cd-income-bank-search" />
          <CommandList>
            <CommandEmpty>No bank found.</CommandEmpty>
            <CommandGroup>
              {INDIAN_BANKS.map((b) => (
                <CommandItem key={b} value={b} onSelect={() => { onChange(b); setOpen(false); }} data-testid={`cd-income-bank-opt-${b}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === b ? 'opacity-100 text-blue-600' : 'opacity-0'}`} />{b}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const DocUpload = ({ leadId, docType, docId, docName, onUploaded, testId, onRead, readLabel }) => {
  const ref = useRef(null);
  const [up, setUp] = useState(false);
  const [reading, setReading] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUp(true);
    try {
      const res = await loanProfilingApi.uploadDocument(leadId, docType, file);
      const d = res.data.document;
      onUploaded(d.id, d.file_name);
      toast.success('Uploaded');
      if (onRead) {
        // Read the card straight away — the rep shouldn't have to press twice.
        setReading(true);
        try {
          const ai = await loanProfilingApi.analyzeDocument(leadId, d.id);
          const fields = ai.data?.ai_data || ai.data?.data || null;
          if (fields && fields.is_readable !== false) onRead(fields);
          else toast.message('Could not read that image — please type the details in.');
        } catch (err) {
          toast.message('Could not read that image — please type the details in.');
        } finally { setReading(false); }
      }
    } catch (err) { toast.error('Upload failed'); }
    finally { setUp(false); if (ref.current) ref.current.value = ''; }
  };

  return (
    <div className="flex items-center gap-2 mt-1.5">
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={handle} data-testid={`${testId}-file`} />
      <Button type="button" size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={up || reading} data-testid={`${testId}-upload`}>
        {(up || reading) ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          : onRead ? <Sparkles className="w-3.5 h-3.5 mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
        {reading ? (readLabel || 'Reading…') : docId ? 'Replace' : onRead ? 'Upload & read' : 'Upload'}
      </Button>
      {docId && (
        <a href={loanProfilingApi.fileUrl(leadId, docId)} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline max-w-[150px] truncate" data-testid={`${testId}-view`}>
          <FileText className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{docName || 'View'}</span>
          <ExternalLink className="w-3 h-3 shrink-0" />
        </a>
      )}
    </div>
  );
};

const SaveBar = ({ onSave, saving, testId }) => (
  <div className="flex justify-end pt-2 border-t border-slate-100">
    <Button onClick={onSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid={testId}>
      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
      {saving ? 'Saving…' : 'Save'}
    </Button>
  </div>
);

// ───────────────────────── personal details ─────────────────────────
const PersonalForm = ({ leadId, personal, income, lead, onChanged }) => {
  const init = useMemo(() => {
    const name = (lead?.customer_name || '').trim();
    const sp = name.indexOf(' ');
    return {
      category: '', pan: '', pan_doc_id: '', pan_doc_name: '', address_proof_type: '',
      address_proof_doc_id: '', address_proof_doc_name: '', national_id: '',
      aadhaar_doc_id: '', aadhaar_doc_name: '',
      first_name: sp > 0 ? name.slice(0, sp) : name,
      last_name: sp > 0 ? name.slice(sp + 1) : '',
      date_of_birth: '', mobile: lead?.customer_phone || lead?.mobile || '',
      gender: '', father_name: '', mother_name: '', marital_status: '',
      stability_current_city: '', email: lead?.email || '', pincode: '', area: '',
      area_lat: null, area_lng: null,
      residence_type: '', own_house_available: '', staying_in_own_house: '',
      property_owner: '', property_owner_name: '',
      electricity_bill_doc_id: '', electricity_bill_doc_name: '',
      relationship_proof_doc_id: '', relationship_proof_doc_name: '',
      // residence/category used to live on the income step — carry the old answers over
      ...Object.fromEntries(Object.entries({
        category: income?.category,
        residence_type: income?.residence_type,
        own_house_available: income?.own_house_available,
        staying_in_own_house: income?.staying_in_own_house,
      }).filter(([, v]) => v)),
      ...(personal || {}),
    };
  }, [personal, income, lead]);
  const [f, setF] = useState(init);
  const [saving, setSaving] = useState(false);
  const [filled, setFilled] = useState([]);   // fields the AI just filled
  const upd = (k, v) => setF((p) => {
    const next = { ...p, [k]: v };
    // Owning the house answers the next two questions in practice — prefill, still editable.
    if (k === 'residence_type' && v === 'Owned') {
      if (!p.own_house_available) next.own_house_available = 'Yes';
      if (!p.staying_in_own_house) next.staying_in_own_house = 'Yes';
    }
    return next;
  });

  const applyRead = (map) => {
    // Work out the touched keys up front — setF's updater runs lazily, so pushing
    // inside it left `touched` empty and the AI badges/toast never showed.
    const clean = Object.fromEntries(Object.entries(map)
      .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
      .filter(([, v]) => v));
    const touched = Object.keys(clean).filter((k) => clean[k] !== f[k]);
    setF((p) => ({ ...p, ...clean }));
    if (touched.length) {
      setFilled((prev) => [...new Set([...prev, ...touched])]);
      toast.success(`Read ${touched.length} field${touched.length > 1 ? 's' : ''} off the card — check before saving`);
    }
  };

  const readPan = (d) => {
    const [first, ...rest] = String(d.name || '').trim().split(/\s+/);
    applyRead({
      pan: String(d.pan_number || '').toUpperCase(),
      first_name: f.first_name || first,
      last_name: f.last_name || rest.join(' '),
      father_name: f.father_name || d.father_name,
      date_of_birth: f.date_of_birth || d.dob,
    });
  };

  const readAadhaar = (d) => {
    const [first, ...rest] = String(d.name || '').trim().split(/\s+/);
    applyRead({
      national_id: String(d.aadhaar_number || '').replace(/\s/g, ''),
      first_name: f.first_name || first,
      last_name: f.last_name || rest.join(' '),
      date_of_birth: f.date_of_birth || d.dob,
      gender: !f.gender && /^(male|female|other)$/i.test(d.gender || '')
        ? d.gender[0].toUpperCase() + d.gender.slice(1).toLowerCase() : f.gender,
      pincode: f.pincode || (String(d.pincode || '').match(/\d{6}/) || [''])[0],
      address_proof_type: f.address_proof_type || 'National ID/UID',
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await loanProfilingApi.savePersonalDetails(leadId, {
        pan: f.pan || '', pan_doc_id: f.pan_doc_id || '', pan_doc_name: f.pan_doc_name || '',
        address_proof_type: f.address_proof_type || '',
        address_proof_doc_id: f.address_proof_doc_id || '', address_proof_doc_name: f.address_proof_doc_name || '',
        national_id: f.national_id || '',
        aadhaar_doc_id: f.aadhaar_doc_id || '', aadhaar_doc_name: f.aadhaar_doc_name || '',
        first_name: f.first_name || '', last_name: f.last_name || '', date_of_birth: f.date_of_birth || '',
        mobile: f.mobile || '', gender: f.gender || '', father_name: f.father_name || '',
        mother_name: f.mother_name || '', marital_status: f.marital_status || '',
        stability_current_city: f.stability_current_city || '', email: f.email || '',
        pincode: f.pincode || '', area: f.area || '',
        area_lat: (f.area_lat != null && f.area_lat !== '') ? parseFloat(f.area_lat) : null,
        area_lng: (f.area_lng != null && f.area_lng !== '') ? parseFloat(f.area_lng) : null,
        residence_type: f.residence_type || '',
        own_house_available: f.own_house_available || '',
        staying_in_own_house: f.staying_in_own_house || '',
        property_owner: f.property_owner || '',
        property_owner_name: f.property_owner_name || '',
        electricity_bill_doc_id: f.electricity_bill_doc_id || '',
        electricity_bill_doc_name: f.electricity_bill_doc_name || '',
        relationship_proof_doc_id: f.relationship_proof_doc_id || '',
        relationship_proof_doc_name: f.relationship_proof_doc_name || '',
      });
      setFilled([]);
      toast.success('Personal details saved');
      if (onChanged) await onChanged();
    } catch (e) { toast.error('Failed to save personal details'); }
    finally { setSaving(false); }
  };

  const aiBadge = (k) => (filled.includes(k) ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 ml-1.5" data-testid={`ai-filled-${k}`}>
      <Sparkles className="w-3 h-3" />AI
    </span>
  ) : null);

  return (
    <motion.div {...reveal} className="flex flex-col gap-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <Field label={<>PAN{aiBadge('pan')}</>} required>
          <Input value={f.pan} onChange={(e) => upd('pan', e.target.value.toUpperCase())} maxLength={10} placeholder="ABCDE1234F" className={`${inputCls} uppercase`} data-testid="cd-pan" />
          <DocUpload leadId={leadId} docType="pan_card" docId={f.pan_doc_id} docName={f.pan_doc_name}
            onUploaded={(id, name) => setF((p) => ({ ...p, pan_doc_id: id, pan_doc_name: name }))}
            onRead={readPan} readLabel="Reading PAN…" testId="cd-pan-doc" />
        </Field>
        <Field label="Address Proof" required>
          <Select value={f.address_proof_type || ''} onValueChange={(v) => upd('address_proof_type', v)}>
            <SelectTrigger className={inputCls} data-testid="cd-address-proof">
              <SelectValue placeholder="Select an address proof" />
            </SelectTrigger>
            <SelectContent>
              {ADDRESS_PROOFS.map((o) => <SelectItem key={o} value={o} data-testid={`cd-address-proof-opt-${o}`}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
          {f.address_proof_type && (
            <DocUpload leadId={leadId} docType="address_proof" docId={f.address_proof_doc_id} docName={f.address_proof_doc_name}
              onUploaded={(id, name) => setF((p) => ({ ...p, address_proof_doc_id: id, address_proof_doc_name: name }))} testId="cd-address-proof-doc" />
          )}
        </Field>
        <Field label={<>Aadhaar / National ID{aiBadge('national_id')}</>} required>
          <Input value={f.national_id} onChange={(e) => upd('national_id', e.target.value)} placeholder="Aadhaar / UID number" className={inputCls} data-testid="cd-national-id" />
          <DocUpload leadId={leadId} docType="aadhaar_card" docId={f.aadhaar_doc_id} docName={f.aadhaar_doc_name}
            onUploaded={(id, name) => setF((p) => ({ ...p, aadhaar_doc_id: id, aadhaar_doc_name: name }))}
            onRead={readAadhaar} readLabel="Reading Aadhaar…" testId="cd-aadhaar-doc" />
        </Field>
        <Field label={<>First Name{aiBadge('first_name')}</>} required>
          <Input value={f.first_name} onChange={(e) => upd('first_name', e.target.value)} className={inputCls} data-testid="cd-first-name" />
        </Field>
        <Field label={<>Last Name{aiBadge('last_name')}</>} required>
          <Input value={f.last_name} onChange={(e) => upd('last_name', e.target.value)} className={inputCls} data-testid="cd-last-name" />
        </Field>
        <Field label={<>Date of Birth{aiBadge('date_of_birth')}</>} required>
          <Input type="date" value={f.date_of_birth} onChange={(e) => upd('date_of_birth', e.target.value)} className={inputCls} data-testid="cd-dob" />
        </Field>
        <Field label="Mobile Number" required>
          <Input value={f.mobile} onChange={(e) => upd('mobile', e.target.value)} className={inputCls} data-testid="cd-mobile" />
        </Field>
        <SelectField label={<>Gender{aiBadge('gender')}</>} required value={f.gender} onChange={(v) => upd('gender', v)} options={GENDERS} testId="cd-gender" />
        <Field label={<>Father's Name{aiBadge('father_name')}</>} required>
          <Input value={f.father_name} onChange={(e) => upd('father_name', e.target.value)} className={inputCls} data-testid="cd-father-name" />
        </Field>
        <Field label="Mother's Name" required>
          <Input value={f.mother_name} onChange={(e) => upd('mother_name', e.target.value)} className={inputCls} data-testid="cd-mother-name" />
        </Field>
        <SelectField label="Marital Status" required value={f.marital_status} onChange={(v) => upd('marital_status', v)} options={MARITAL} testId="cd-marital-status" />
        <SelectField label="Stability in Current City" required value={f.stability_current_city} onChange={(v) => upd('stability_current_city', v)} options={STABILITY} testId="cd-stability" />
        <Field label="Email Address" required>
          <Input type="email" value={f.email} onChange={(e) => upd('email', e.target.value)} className={inputCls} data-testid="cd-email" />
        </Field>
        <Field label={<>Pincode{aiBadge('pincode')}</>} required>
          <Input value={f.pincode} onChange={(e) => upd('pincode', e.target.value)} maxLength={6} className={inputCls} data-testid="cd-pincode" />
        </Field>
        <Field label="Area" required>
          <PlacesAutocomplete
            value={f.area}
            onChange={(v) => upd('area', v)}
            onSelect={({ address, latitude, longitude }) => setF((p) => ({
              ...p,
              area: address || p.area,
              area_lat: latitude ?? null,
              area_lng: longitude ?? null,
            }))}
            placeholder="Search locality / area"
            className={inputCls}
          />
          {(f.area_lat != null && f.area_lng != null) && (
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 mt-1" data-testid="cd-area-coords">
              <MapPin className="w-3 h-3" />{Number(f.area_lat).toFixed(5)}, {Number(f.area_lng).toFixed(5)}
            </span>
          )}
        </Field>
      </div>

      {/* Residence — a KYC fact, and the property surrogate for no-income-proof cases */}
      <div className="flex flex-col gap-4" data-testid="residence-section">
        <div className="border-t border-slate-100 pt-4">
          <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Home className="w-4 h-4 text-blue-600" />Residence</h4>
          <p className="text-xs text-slate-400 mt-0.5">Own property is what carries a case with no income proof — the EB bill is the proof banks accept, and a house in a parent's name needs the relationship proved.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <SelectField label="Residence Type" required value={f.residence_type} onChange={(v) => upd('residence_type', v)} options={RESIDENCE} testId="cd-residence-type" />
          <SelectField label="Own house available" value={f.own_house_available} onChange={(v) => upd('own_house_available', v)} options={YES_NO} testId="cd-own-house" />
          <SelectField label="Currently staying in own house" value={f.staying_in_own_house} onChange={(v) => upd('staying_in_own_house', v)} options={YES_NO} testId="cd-staying-own-house" />
          {f.own_house_available === 'Yes' && (
            <>
              <SelectField label="Property is in whose name" required value={f.property_owner}
                onChange={(v) => upd('property_owner', v)} options={PROPERTY_OWNERS} testId="cd-property-owner" />
              {f.property_owner && f.property_owner !== 'Self' && (
                <Field label="Owner's name as on the document" required>
                  <Input value={f.property_owner_name || ''} onChange={(e) => upd('property_owner_name', e.target.value)}
                    className={inputCls} data-testid="cd-property-owner-name" />
                </Field>
              )}
              <Field label="Electricity bill of the own house" required>
                <DocUpload leadId={leadId} docType="property_address_proof"
                  docId={f.electricity_bill_doc_id} docName={f.electricity_bill_doc_name}
                  onUploaded={(id, name) => setF((p) => ({ ...p, electricity_bill_doc_id: id, electricity_bill_doc_name: name }))}
                  testId="cd-electricity-bill" />
              </Field>
              {f.property_owner && f.property_owner !== 'Self' && (
                <Field label={`Proof of relationship with ${f.property_owner.toLowerCase()}`} required>
                  <DocUpload leadId={leadId} docType="relationship_proof"
                    docId={f.relationship_proof_doc_id} docName={f.relationship_proof_doc_name}
                    onUploaded={(id, name) => setF((p) => ({ ...p, relationship_proof_doc_id: id, relationship_proof_doc_name: name }))}
                    testId="cd-relationship-proof" />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Ration card, passport, birth certificate or a family certificate — Aadhaar alone rarely carries the father's name.
                  </p>
                </Field>
              )}
            </>
          )}
        </div>
      </div>

      <SaveBar onSave={save} saving={saving} testId="save-personal-details-button" />
    </motion.div>
  );
};

// ───────────────────────── co-applicant card ─────────────────────────
const CoApplicantCard = ({ idx, data, onChange, onRemove, leadId }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid={`coapplicant-card-${idx}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Users className="w-4 h-4 text-blue-600" /> Co-applicant {idx + 1}
      </div>
      <Button type="button" size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50 h-8" onClick={() => onRemove(idx)} data-testid={`coapplicant-remove-${idx}`}>
        <Trash2 className="w-3.5 h-3.5 mr-1.5" />Remove
      </Button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      <Field label="Co-applicant Name" required>
        <Input value={data.name || ''} onChange={(e) => onChange('name', e.target.value)} className={inputCls} data-testid={`coapplicant-${idx}-name`} />
      </Field>
      <SelectField label="Relationship" required value={data.relationship} onChange={(v) => onChange('relationship', v)} options={RELATIONSHIPS} testId={`coapplicant-${idx}-relationship`} />
      <Field label="Mobile" required>
        <Input value={data.mobile || ''} onChange={(e) => onChange('mobile', e.target.value)} className={inputCls} data-testid={`coapplicant-${idx}-mobile`} />
      </Field>
      <Field label="CIBIL Score">
        <Input type="number" min={300} max={900} value={data.cibil_score ?? ''} onChange={(e) => onChange('cibil_score', e.target.value)} placeholder="300–900" className={inputCls} data-testid={`coapplicant-${idx}-cibil`} />
      </Field>
      <SelectField label="Category" value={data.category} onChange={(v) => onChange('category', v)} options={CATEGORIES} testId={`coapplicant-${idx}-category`} />
      <SelectField label="Income Proof" value={data.income_proof} onChange={(v) => onChange('income_proof', v)} options={INCOME_PROOF} testId={`coapplicant-${idx}-income-proof`} />
      <Field label="Monthly Income (₹)">
        <Input type="number" value={data.monthly_income ?? ''} onChange={(e) => onChange('monthly_income', e.target.value)} className={inputCls} data-testid={`coapplicant-${idx}-monthly-income`} />
      </Field>
      <Field label="Bank Statement">
        <DocUpload leadId={leadId} docType="coapplicant_bank_statement" docId={data.bank_statement_doc_id} docName={data.bank_statement_doc_name}
          onUploaded={(id, name) => { onChange('bank_statement_doc_id', id); onChange('bank_statement_doc_name', name); }} testId={`coapplicant-${idx}-bank-statement`} />
      </Field>
    </div>
  </div>
);

// ───────────────────────── category-specific income fields ─────────────────────────
const CategoryFields = ({ leadId, category, values, onChange }) => {
  const spec = CATEGORY_FIELDS[category] || [];
  if (!category) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center" data-testid="category-missing-notice">
        <p className="text-sm text-slate-600 font-medium">Pick how the customer earns above.</p>
        <p className="text-xs text-slate-400 mt-1">Salaried, business, agriculture and pension cases are each asked different questions.</p>
      </div>
    );
  }
  const visible = spec.filter((fld) => (!fld.when
      || Object.entries(fld.when).every(([k, v]) => values[k] === v))
    && (!fld.unless
      || !Object.entries(fld.unless).every(([k, v]) => v.includes(values[k] ?? ''))));

  return (
    <div className="flex flex-col gap-4" data-testid={`category-fields-${category}`}>
      <div className="border-t border-slate-100 pt-4">
        <h4 className="text-sm font-semibold text-slate-800">{category} details</h4>
        <p className="text-xs text-slate-400 mt-0.5">What a bank asks a {category.toLowerCase()} applicant for.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {visible.map((fld) => {
          const tid = `cd-cat-${fld.key}`;
          if (fld.type === 'select') {
            return <SelectField key={fld.key} label={fld.label} value={values[fld.key]}
              onChange={(v) => onChange(fld.key, v)} options={fld.options} testId={tid} />;
          }
          if (fld.type === 'bank') {
            return (
              <Field key={fld.key} label={fld.label}>
                <BankCombobox value={values[fld.key]} onChange={(v) => onChange(fld.key, v)} testId={tid} />
              </Field>
            );
          }
          if (fld.type === 'doc') {
            return (
              <Field key={fld.key} label={fld.label}>
                <DocUpload leadId={leadId} docType={fld.docType}
                  docId={values[`${fld.key}_doc_id`]} docName={values[`${fld.key}_doc_name`]}
                  onUploaded={(id, name) => { onChange(`${fld.key}_doc_id`, id); onChange(`${fld.key}_doc_name`, name); }}
                  testId={tid} />
              </Field>
            );
          }
          return (
            <Field key={fld.key} label={fld.label}>
              <Input type={fld.type === 'number' ? 'number' : 'text'} value={values[fld.key] ?? ''}
                onChange={(e) => onChange(fld.key, e.target.value)} className={inputCls} data-testid={tid} />
            </Field>
          );
        })}
      </div>
    </div>
  );
};

// Only the current category's answers are saved — a rep who switches category
// shouldn't carry the previous category's turnover into a salaried case.
const prunedCategoryDetails = (category, details = {}) => {
  const keys = (CATEGORY_FIELDS[category] || []).map((fld) => fld.key);
  const allowed = new Set(keys.flatMap((k) => [k, `${k}_doc_id`, `${k}_doc_name`]));
  return Object.fromEntries(Object.entries(details || {}).filter(([k]) => allowed.has(k)));
};

// ───────────────────────── income details ─────────────────────────
const IncomeForm = ({ leadId, income, personal, caseProfile, earningProfiles, onPatchProfile, onChanged }) => {
  // The earning answer owns the category now; personal.category is only a legacy fallback.
  const category = caseProfile?.income_category
    || ((caseProfile?.earning_profile || caseProfile?.income_type) ? '' : (personal?.category || income?.category || ''));
  const noIncome = (caseProfile?.earning_profile || '') === 'NO_INCOME'
    || (!caseProfile?.earning_profile && caseProfile?.income_type === 'NIP');
  const [f, setF] = useState(() => ({
    income_proof: '', monthly_income: '', income_credited_to: '',
    max_emi_affordability: '',
    bank_statement_doc_id: '', bank_statement_doc_name: '',
    category_details: {}, co_applicants: [], ...(income || {}),
  }));
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const updCat = (k, v) => setF((p) => ({ ...p, category_details: { ...(p.category_details || {}), [k]: v } }));
  const coApps = f.co_applicants || [];
  const addCoApplicant = () => setF((p) => ({ ...p, co_applicants: [...(p.co_applicants || []), blankCoApplicant()] }));
  const updCoApplicant = (idx, key, val) => setF((p) => {
    const arr = [...(p.co_applicants || [])];
    arr[idx] = { ...arr[idx], [key]: val };
    return { ...p, co_applicants: arr };
  });
  const removeCoApplicant = (idx) => setF((p) => ({ ...p, co_applicants: (p.co_applicants || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      // A no-income file must not carry a salary: the hidden fields are cleared,
      // not quietly posted from whatever the form held before the answer changed.
      await loanProfilingApi.saveIncomeDetails(leadId, {
        income_proof: noIncome ? '' : (f.income_proof || ''),
        category: noIncome ? '' : (category || ''),
        monthly_income: noIncome ? null
          : ((f.monthly_income !== '' && f.monthly_income != null) ? parseFloat(f.monthly_income) : null),
        income_credited_to: noIncome ? '' : (f.income_credited_to || ''),
        max_emi_affordability: (f.max_emi_affordability !== '' && f.max_emi_affordability != null) ? parseFloat(f.max_emi_affordability) : null,
        category_details: noIncome ? {} : prunedCategoryDetails(category, f.category_details),
        bank_statement_doc_id: f.bank_statement_doc_id || '',
        bank_statement_doc_name: f.bank_statement_doc_name || '',
        co_applicants: (f.co_applicants || []).map((c) => ({
          name: c.name || '',
          relationship: c.relationship || '',
          mobile: c.mobile || '',
          cibil_score: (c.cibil_score !== '' && c.cibil_score != null) ? parseInt(c.cibil_score, 10) : null,
          category: c.category || '',
          income_proof: c.income_proof || '',
          monthly_income: (c.monthly_income !== '' && c.monthly_income != null) ? parseFloat(c.monthly_income) : null,
          bank_statement_doc_id: c.bank_statement_doc_id || '',
          bank_statement_doc_name: c.bank_statement_doc_name || '',
        })),
      });
      toast.success('Income details saved');
      if (onChanged) await onChanged();
    } catch (e) { toast.error('Failed to save income details'); }
    finally { setSaving(false); }
  };

  return (
    <motion.div {...reveal} className="flex flex-col gap-6">
      {onPatchProfile && (
        <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4" data-testid="income-profile-section">
          <IncomeProfileBlock profile={caseProfile} onPatch={onPatchProfile}
            earningProfiles={earningProfiles} personal={personal} />
        </div>
      )}

      {noIncome ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="no-income-fields">
          <Field label="Maximum EMI the customer can pay (₹)" required>
            <Input type="number" value={f.max_emi_affordability ?? ''} onChange={(e) => upd('max_emi_affordability', e.target.value)} className={inputCls} data-testid="cd-max-emi" />
          </Field>
          <Field label="Last 6 Months Bank Statement">
            <DocUpload leadId={leadId} docType="bank_statement" docId={f.bank_statement_doc_id} docName={f.bank_statement_doc_name}
              onUploaded={(id, name) => setF((p) => ({ ...p, bank_statement_doc_id: id, bank_statement_doc_name: name }))} testId="cd-bank-statement" />
          </Field>
          <p className="text-xs text-slate-500 self-end pb-2 lg:col-span-1">
            No income proof on this file — the property, guarantor or co-applicant below is what a bank will lend against.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <SelectField label="Income Proof" required value={f.income_proof} onChange={(v) => upd('income_proof', v)} options={INCOME_PROOF} testId="cd-income-proof" />
          <Field label="Monthly Income (₹)" required>
            <Input type="number" value={f.monthly_income ?? ''} onChange={(e) => upd('monthly_income', e.target.value)} className={inputCls} data-testid="cd-monthly-income" />
          </Field>
          <Field label="Income Credited To" required>
            <BankCombobox value={f.income_credited_to} onChange={(v) => upd('income_credited_to', v)} testId="cd-income-bank" />
          </Field>
          <Field label="Maximum EMI Affordability (₹)" required>
            <Input type="number" value={f.max_emi_affordability ?? ''} onChange={(e) => upd('max_emi_affordability', e.target.value)} className={inputCls} data-testid="cd-max-emi" />
          </Field>
          <Field label="Last 6 Months Bank Statement">
            <DocUpload leadId={leadId} docType="bank_statement" docId={f.bank_statement_doc_id} docName={f.bank_statement_doc_name}
              onUploaded={(id, name) => setF((p) => ({ ...p, bank_statement_doc_id: id, bank_statement_doc_name: name }))} testId="cd-bank-statement" />
          </Field>
        </div>
      )}

      {!noIncome && <CategoryFields leadId={leadId} category={category} values={f.category_details || {}} onChange={updCat} />}

      {/* Co-applicants — added when the buyer's credit/income alone isn't sufficient */}
      <div className="flex flex-col gap-4" data-testid="coapplicants-section">
        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div>
            <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Users className="w-4 h-4 text-blue-600" />Co-applicants</h4>
            <p className="text-xs text-slate-400 mt-0.5">Add a co-applicant when the buyer's credit or income alone isn't enough for the loan.</p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addCoApplicant} data-testid="coapplicant-add-button">
            <UserPlus className="w-3.5 h-3.5 mr-1.5" />Add co-applicant
          </Button>
        </div>
        {coApps.map((c, i) => (
          <CoApplicantCard key={i} idx={i} data={c} leadId={leadId}
            onChange={(k, v) => updCoApplicant(i, k, v)} onRemove={removeCoApplicant} />
        ))}
      </div>

      <SaveBar onSave={save} saving={saving} testId="save-income-details-button" />
    </motion.div>
  );
};

// ───────────────────────── block ─────────────────────────
export const CustomerDetailsBlock = ({ leadId, personal, income, lead, caseProfile, earningProfiles, onPatchProfile, documents = [], checklist = [], onChanged }) => (
  <div data-testid="customer-details-block">
    <SectionHeading hint="Applicant KYC, income, bureau and banking — everything captured on the customer.">Customer Details</SectionHeading>
    <Tabs defaultValue="personal" className="w-full">
      <TabsList className="flex space-x-1 rounded-lg bg-slate-100 p-1 mb-5 max-w-3xl">
        <TabsTrigger value="personal" className="flex-1" data-testid="tab-personal-details">
          <User className="w-3.5 h-3.5 mr-1.5" />Personal details
        </TabsTrigger>
        <TabsTrigger value="income" className="flex-1" data-testid="tab-income-details">
          <Wallet className="w-3.5 h-3.5 mr-1.5" />Income details
        </TabsTrigger>
        <TabsTrigger value="cibil" className="flex-1" data-testid="tab-cibil-analysis">
          <Gauge className="w-3.5 h-3.5 mr-1.5" />CIBIL analysis
        </TabsTrigger>
        <TabsTrigger value="bank-statement" className="flex-1" data-testid="tab-bank-statement-analysis">
          <Landmark className="w-3.5 h-3.5 mr-1.5" />Bank statement analysis
        </TabsTrigger>
        <TabsTrigger value="documents" className="flex-1" data-testid="tab-documents">
          <FolderOpen className="w-3.5 h-3.5 mr-1.5" />Documents
        </TabsTrigger>
      </TabsList>
      <TabsContent value="personal" forceMount className="data-[state=inactive]:hidden">
        <PersonalForm leadId={leadId} personal={personal} income={income} lead={lead} onChanged={onChanged} />
      </TabsContent>
      <TabsContent value="income" forceMount className="data-[state=inactive]:hidden">
        <IncomeForm leadId={leadId} income={income} personal={personal}
          caseProfile={caseProfile} earningProfiles={earningProfiles}
          onPatchProfile={onPatchProfile} onChanged={onChanged} />
      </TabsContent>
      <TabsContent value="cibil">
        <CibilAnalysis leadId={leadId} lead={lead} personal={personal} documents={documents} onChanged={onChanged} />
      </TabsContent>
      <TabsContent value="bank-statement">
        <BankStatementAnalysis leadId={leadId} income={income} onChanged={onChanged} />
      </TabsContent>
      <TabsContent value="documents">
        <DocumentsTab leadId={leadId} checklist={checklist} documents={documents} onChanged={onChanged} />
      </TabsContent>
    </Tabs>
  </div>
);

export default CustomerDetailsBlock;
